import { createFileRoute } from '@tanstack/react-router'
import { useEvent, useEventUsers, useUser, useAddCredit, useUserTransactionMetrics, useUserByQrCode, useEventTerminals, useTerminalReload } from '@/lib/api/queries'
import { apiErrorDetail } from '@/lib/api/fraud-client'
import { useAuth } from '@/lib/auth'
import { apiClient } from '@/lib/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, User, DollarSign, Check, CreditCard, Eye, QrCode, ArrowLeft } from 'lucide-react'
import { useState, useMemo, useEffect, useRef } from 'react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { useModal } from '@/components/modals/modal-container'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { QRScannerModal } from '@/components/qr/qr-scanner-modal'
import { FinixReader, isFinixReaderAvailable } from '@/lib/finix-reader'

// Attempt ids whose reader operation is LIVE in this JS context. Module
// scope on purpose: route remounts (event switches) keep the running
// closure and this registry, while a refresh/crash starts empty — which is
// exactly the discriminator recovery needs. A LIVE operation must never be
// resolved as "no charge found" (the tap could land seconds later); a
// crash-recovered record (not in this set) gets the manual resolution paths.
const liveSdkAttemptIds = new Set<number>()

// Module-wide SDK-flow mutex. A per-component ref resets on route remount
// (event switch) while the initial reload POST is still pending — before
// any attempt id exists, so before the registry and recovery marker can
// cover it. This lock closes that window: one SDK charge flow per tab,
// full stop, from before the POST until the flow settles.
let sdkChargeFlowActive = false

export const Route = createFileRoute('/_authenticated/events/$eventId/reload')({
  component: ReloadPage,
  // Everything on this page is event-scoped — the persisted SDK recovery
  // marker, the remembered terminal, selected user, amounts. The router
  // reuses the component when only $eventId changes, which would leave
  // event A's state (and its charge-block hydration) live on event B;
  // remounting re-runs every initializer against the new event's storage.
  remountDeps: ({ params }) => params.eventId,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      userId: search.userId ? Number(search.userId) : undefined,
    }
  },
})

function ReloadPage() {
  const { eventId } = Route.useParams()
  const { userId: preselectedUserId } = Route.useSearch()
  const eventIdNum = Number(eventId)
  
  // Permission checks
  const canViewReload = usePermission('can_view_reload')
  const canTransfer = usePermission('can_transfer')
  const canRefundAddcredit = usePermission('can_refund_addcredit')
  void canTransfer // Future use
  
  // All hooks must be called before any early returns
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300) // 300ms delay
  const [showScanner, setShowScanner] = useState(false)
  const [qrError, setQrError] = useState('')
  const [selectedUser, setSelectedUser] = useState<number | null>(preselectedUserId || null)
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [customAmount, setCustomAmount] = useState('')
  const [showCustomAmount, setShowCustomAmount] = useState(false)
  // The station's terminal choice survives refresh: per-browser, per-event —
  // two laptops at two counters keep different terminals.
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(() => {
    const stored = localStorage.getItem(`reload-device:${eventId}`)
    return stored ? Number(stored) : null
  })
  // One Checkout button; method choice, terminal status, success and errors
  // all live in the checkout modal.
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutStage, setCheckoutStage] =
    useState<'method' | 'terminal' | 'waiting' | 'manual-processing' | 'success' | 'error'>('method')
  const [checkoutError, setCheckoutError] = useState('')
  const [checkoutBalance, setCheckoutBalance] = useState<number | null>(null)
  // SDK-reader progress line shown in the waiting stage ('' = server-push).
  const [waitingStatus, setWaitingStatus] = useState('')
  // Confirmation-only recovery: from the moment an SDK charge is in flight,
  // a persisted STATION-WIDE marker blocks new charges until the attempt is
  // settled — armed BEFORE the tap so a crash, tab close, or double-click
  // cannot open a double-charge window; refreshed with the transfer id the
  // moment the reader reports one. Global key on purpose: switching events
  // must not sidestep the block, so the record carries its originating
  // eventId and every resolution call targets THAT event. transferId null =
  // no id in hand; chargeReported = a transfer definitely exists (never
  // offer "no charge found" then).
  type SdkRecovery = { eventId: number; attemptId: number; transferId: string | null; chargeReported: boolean }
  const sdkRecoveryKey = 'sdk-recovery'
  const [sdkRecovery, setSdkRecoveryState] = useState<SdkRecovery | null>(() => {
    try {
      const raw = localStorage.getItem('sdk-recovery')
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (typeof parsed.eventId !== 'number') return null
      return {
        eventId: parsed.eventId,
        attemptId: parsed.attemptId,
        transferId: parsed.transferId ?? null,
        chargeReported: parsed.chargeReported ?? !!parsed.transferId,
      }
    } catch {
      return null
    }
  })
  const setSdkRecovery = (value: SdkRecovery | null) => {
    setSdkRecoveryState(value)
    try {
      if (value) localStorage.setItem(sdkRecoveryKey, JSON.stringify(value))
      else localStorage.removeItem(sdkRecoveryKey)
    } catch {
      // storage unavailable — in-memory guard still applies
    }
  }
  // Attempt currently being run on an SDK reader (for attempt-scoped cancel).
  const sdkAttemptIdRef = useRef<number | null>(null)
  // Transfer id typed by staff from the Finix dashboard when resolving an
  // ambiguous (transfer-less) recovery.
  const [recoveryTransferId, setRecoveryTransferId] = useState('')
  // Whether the recovery record's reader operation is RUNNING in this tab
  // (an event switch mid-tap keeps the closure + module registry alive).
  // State, not a derived const: the registry is not reactive, so the
  // watcher below re-checks BOTH the registry (op can end without touching
  // the stored record) and storage (the running closure updates/clears the
  // record) — the panel transitions the moment either changes.
  const [recoveryIsLive, setRecoveryIsLive] = useState<boolean>(
    () => !!sdkRecovery && liveSdkAttemptIds.has(sdkRecovery.attemptId))
  useEffect(() => {
    const live = !!sdkRecovery && liveSdkAttemptIds.has(sdkRecovery.attemptId)
    setRecoveryIsLive(live)
    if (!live) return
    const timer = setInterval(() => {
      try {
        const raw = localStorage.getItem(sdkRecoveryKey)
        const next = raw ? JSON.parse(raw) : null
        if (JSON.stringify(next) !== JSON.stringify(sdkRecovery)) {
          setSdkRecoveryState(next)
          if (!next && checkoutStage === 'error') {
            setCheckoutError('')
            setCheckoutStage('method')
          }
        } else if (!liveSdkAttemptIds.has(sdkRecovery!.attemptId)) {
          // Operation finished without changing the stored record — flip
          // liveness so the manual resolution paths appear.
          setRecoveryIsLive(false)
        }
      } catch {
        // storage unavailable — nothing to sync
      }
    }, 1500)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkRecovery, checkoutStage, recoveryIsLive])
  // Reader rejection codes that PROVE no charge was submitted. 'canceled'
  // is deliberately NOT here: a cancel racing the cardholder's tap can
  // still leave a submitted transfer — it resolves through recovery (one
  // extra click) instead of silently unblocking.
  const READER_SAFE_CANCEL_CODES = ['UNAVAILABLE', 'UNIMPLEMENTED', 'not_initialized', 'not_connected', 'reader_unavailable', 'connection_failed']
  // Confirm-rejection codes proving the submitted id references a REAL
  // transfer at Finix (it exists but failed verification for this attempt).
  // Money-safety bias: once a real transfer is in play, "no charge found"
  // is off the table even for a manual entry.
  const TRANSFER_EXISTS_CODES = [
    'transfer_amount_mismatch', 'transfer_currency_mismatch',
    'transfer_device_mismatch', 'transfer_merchant_mismatch',
    'transfer_attempt_mismatch', 'transfer_already_used',
    'attempt_transfer_mismatch',
  ]
  const [success, setSuccess] = useState(false)
  const [showCashModal, setShowCashModal] = useState(false)
  const [cashReceived, setCashReceived] = useState('')
  // A refused cash reload (blocked attendee) must be told to the operator.
  const [cashError, setCashError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processTimeout, setProcessTimeout] = useState(0)
  
  useEffect(() => {
    if (selectedDeviceId != null) {
      localStorage.setItem(`reload-device:${eventId}`, String(selectedDeviceId))
    }
  }, [selectedDeviceId, eventId])

  const { data: event } = useEvent(eventIdNum)
  // Only fetch users when there's a search term
  const { data: usersData, isLoading } = useEventUsers(eventIdNum, {
    search: debouncedSearch || undefined,
    page_size: 10,
  }, { enabled: !!debouncedSearch })
  // Resolve the selected user by ID. Covers every entry point — URL preselect
  // (from support), a QR scan, or a search click — where we only have the user
  // ID and not a full user object in the search results list. Looked up by ID
  // directly because the user-search endpoint matches name/email/phone, not the
  // numeric user ID.
  const { data: selectedUserFetched } = useUser(selectedUser || 0)
  const { token } = useAuth()
  const addCredit = useAddCredit()
  const terminalReload = useTerminalReload()
  const { data: terminals } = useEventTerminals(eventIdNum)
  const qrLookup = useUserByQrCode()
  const { openUserModal } = useModal()
  
  // Event token configuration
  const tokenValue = event?.token_value || 2
  
  // Get accurate balance for selected user using transaction metrics
  const { data: userMetrics } = useUserTransactionMetrics(
    selectedUser || 0, 
    eventIdNum, 
    tokenValue
  )
  
  // Parse topup amounts from event
  const topupAmounts = useMemo(() => {
    if (event?.topup_amounts) {
      return event.topup_amounts.split(',').map(a => parseInt(a.trim())).filter(a => !isNaN(a) && a > 0)
    }
    // Default amounts if not configured
    return [50, 75, 100, 125]
  }, [event?.topup_amounts])
  
  // Permission guard - now safe after all hooks
  if (!canViewReload) {
    return <AccessDenied message="You don't have permission to access reload functions." />
  }

  const users = usersData?.results || []
  // Prefer the search-result object (it carries balance); otherwise use the
  // by-ID fetch. Guard the fetch against the active selection so a stale result
  // doesn't linger after Clear or switching users.
  const selectedUserData =
    users.find(u => u.id === selectedUser) ||
    (selectedUserFetched?.id === selectedUser ? selectedUserFetched : undefined)

  const handleQrScan = async (code: string) => {
    if (!code.trim()) return
    try {
      setQrError('')
      const user = await qrLookup.mutateAsync({ qrCode: code.trim() })
      setShowScanner(false)
      setSelectedUser(user.id)
    } catch (err: any) {
      setQrError(err?.message || 'No user found for that QR code')
    }
  }
  
  const taxPercentage = event?.tax_percentage || 0
  const taxPaidBy = event?.tax_paid_by || 'event'
  const additionalFeePercentage = event?.additional_fee_percentage || 0
  const additionalFeeFixed = event?.additional_fee_fixed || 0
  const currency = event?.currency || 'CAD'

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(value)
  }
  
  // Get the reload amount (from preset or custom)
  const getReloadAmount = () => {
    if (showCustomAmount && customAmount) {
      return parseInt(customAmount) || 0
    }
    return selectedAmount || 0
  }
  
  // Calculate costs
  const getBaseCost = () => {
    return getReloadAmount() * tokenValue
  }
  
  const getTaxAmount = () => {
    // Only apply tax if tax_paid_by is "user"
    if (taxPaidBy !== 'user') {
      return 0
    }
    return getBaseCost() * taxPercentage
  }
  
  const getAdditionalFixedFee = () => additionalFeeFixed || 0
  
  const getAdditionalPercentageFee = () => {
    return getBaseCost() * (additionalFeePercentage || 0)
  }
  
  const getTotalAdditionalFees = () => {
    return getAdditionalFixedFee() + getAdditionalPercentageFee()
  }
  
  const getTotalCost = () => {
    return getBaseCost() + getTaxAmount() + getTotalAdditionalFees()
  }
  
  // Cash change calculation
  const getChange = () => {
    const received = parseFloat(cashReceived) || 0
    const change = Math.max(0, received - getTotalCost())
    // Nickel rounding (round to nearest 5 cents)
    return Math.round(change * 20) / 20
  }
  
  // Quick cash amounts for modal
  const getQuickCashAmounts = () => {
    const base = getTotalCost()
    const amounts = [
      base,
      Math.ceil(base / 5) * 5,
      Math.ceil(base / 5) * 5 + 5,
      Math.ceil(base / 5) * 5 + 10,
      Math.ceil(base / 5) * 5 + 15,
      Math.ceil(base / 5) * 5 + 20
    ]
    // Filter unique amounts
    return amounts.filter((amount, index, arr) => arr.indexOf(amount) === index)
  }

  const resetAmounts = () => {
    setSelectedAmount(null)
    setCustomAmount('')
    setShowCustomAmount(false)
  }

  const selectedDevice = terminals?.find((d) => d.id === selectedDeviceId)
  const isSdkDevice =
    (selectedDevice?.capabilities as Record<string, unknown> | undefined)?.interaction === 'sdk_local'

  // Ambiguous-recovery resolution, path 2: staff checked Finix and found NO
  // charge — cancel the attempt server-side (CAS from pending). The block
  // clears ONLY when the server confirms a settled state; a swallowed
  // network error or a still-live attempt keeps the lock.
  const resolveNoChargeFound = async () => {
    // Never reachable when a transfer was reported or the reader operation
    // is still LIVE (UI hides the button) — these guards are the belt to
    // that suspender. A live tap could land seconds after a "no charge"
    // verdict.
    if (!sdkRecovery || sdkRecovery.chargeReported) return
    if (liveSdkAttemptIds.has(sdkRecovery.attemptId)) return
    try {
      const attempt = await apiClient.cancelTerminalAttempt(token ?? '', sdkRecovery.eventId, sdkRecovery.attemptId)
      if (attempt.state === 'canceled') {
        setSdkRecovery(null)
        setRecoveryTransferId('')
        setCheckoutError('')
        setCheckoutStage('method')
      } else if (attempt.state === 'succeeded') {
        // A confirmation landed after all — the charge IS credited. That
        // resolves the recovery as a success, not a cancellation.
        setSdkRecovery(null)
        setRecoveryTransferId('')
        setCheckoutBalance(attempt.balance ?? null)
        setCheckoutStage('success')
        resetAmounts()
      } else if (attempt.state === 'failed' || attempt.state === 'timed_out') {
        // Settled server-side without a credit — the block has done its job.
        setSdkRecovery(null)
        setRecoveryTransferId('')
        setCheckoutError(`The attempt resolved as ${attempt.state} — no credit was written.`)
      } else {
        setCheckoutError(
          `The attempt is still ${attempt.state} and could not be canceled — try again or confirm with a transfer ID.`)
      }
    } catch (error) {
      setCheckoutError(
        `${error instanceof Error ? error.message : 'Cancellation failed.'} Still blocked — the attempt was not settled.`)
    }
  }

  // An SDK flow whose reload POST hasn't returned yet has no attempt id,
  // no registry entry, and no recovery marker — the module mutex is the
  // only thing covering that window across remounts. Surface it instead of
  // starting anything.
  const surfaceActiveSdkCharge = () => {
    if (!sdkChargeFlowActive) return false
    setCheckoutError('A card payment is already in progress on this station — wait for it to finish before starting another payment.')
    setCheckoutStage('error')
    return true
  }

  // Unresolved SDK charge blocks ALL new terminal charges at this station
  // until resolved — surfacing the recovery UI instead of charging.
  const surfaceSdkRecovery = () => {
    if (!sdkRecovery) return false
    const crossEvent = sdkRecovery.eventId !== eventIdNum ? ' (from another event on this station)' : ''
    if (liveSdkAttemptIds.has(sdkRecovery.attemptId)) {
      setCheckoutError(
        `A card payment${crossEvent} is still running on the reader. Wait for it to finish or cancel it before starting anything else.`)
    } else {
      setCheckoutError(sdkRecovery.chargeReported
        ? `An earlier card payment${crossEvent} was charged but not confirmed. Resolve it — do not start a new payment.`
        : `An earlier card payment${crossEvent} did not settle and may have been charged. Resolve it before charging again.`)
    }
    setCheckoutStage('error')
    return true
  }

  // Terminal: a REAL charge — the request resolves when the cardholder taps
  // (or declines/cancels). The waiting stage owns cancel.
  const startTerminalCharge = async () => {
    if (!selectedUser || !selectedDeviceId) return
    if (surfaceActiveSdkCharge() || surfaceSdkRecovery()) return
    if (isSdkDevice) return startSdkReaderCharge()
    setCheckoutStage('waiting')
    try {
      const attempt = await terminalReload.mutateAsync({
        eventId: eventIdNum,
        deviceId: selectedDeviceId,
        userId: selectedUser,
        numTokens: getReloadAmount(),
        idempotencyKey: crypto.randomUUID(),
      })
      if (attempt.state === 'succeeded') {
        setCheckoutBalance(attempt.balance ?? null)
        setCheckoutStage('success')
        resetAmounts()
      } else {
        setCheckoutError(
          attempt.failure_message || attempt.failure_code || `Payment ${attempt.state}.`)
        setCheckoutStage('error')
      }
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : 'Terminal charge failed.')
      setCheckoutStage('error')
    }
  }

  // Verify the SDK-created transfer server-side and settle the UI. Retries
  // ONLY the verification while the transfer settles at Finix — never the
  // charge itself. On failure it arms confirmation-only recovery: the card
  // IS charged at this point, so the error stage must offer "retry
  // confirmation", never a fresh charge.
  // chargeKnown: whether a transfer was REPORTED by the reader (vs. a
  // manually entered id on an ambiguous recovery). A wrong manual id must
  // not upgrade the recovery to charge-known — that would permanently hide
  // "no charge found" and lock the station on a typo. attemptEventId is the
  // attempt's OWN event — resolution may run from a different event's page.
  const confirmSdkAttempt = async (attemptEventId: number, attemptId: number, transferId: string, chargeKnown = true) => {
    setCheckoutStage('waiting')
    setWaitingStatus('Confirming payment…')
    try {
      let confirmed = null
      for (let tries = 0; ; tries++) {
        try {
          confirmed = await apiClient.confirmTerminalAttempt(
            token ?? '', attemptEventId, attemptId, transferId)
          break
        } catch (error) {
          const message = error instanceof Error ? error.message : ''
          const code = (error as { data?: { code?: string } }).data?.code ?? ''
          if (tries < 4 && (code === 'transfer_pending' || message.includes('not finished processing'))) {
            await new Promise((resolve) => setTimeout(resolve, 1500))
            continue
          }
          throw error
        }
      }
      // Resolved at the provider — recovery no longer applies, whatever the
      // outcome (a failed/canceled transfer was definitively not a charge).
      setSdkRecovery(null)
      setRecoveryTransferId('')
      sdkAttemptIdRef.current = null
      if (confirmed.state === 'succeeded') {
        setCheckoutBalance(confirmed.balance ?? null)
        setCheckoutStage('success')
        resetAmounts()
      } else {
        setCheckoutError(
          confirmed.failure_message || confirmed.failure_code || `Payment ${confirmed.state}.`)
        setCheckoutStage('error')
      }
    } catch (error) {
      const message = (error instanceof Error ? error.message : '') || 'Confirmation failed.'
      const status = (error as { status?: number }).status
      const code = (error as { data?: { code?: string } }).data?.code ?? ''
      const stillPending = code === 'transfer_pending' || message.includes('not finished processing')
      if (status === 400 && !stillPending) {
        // Definitive server rejection — this id cannot confirm this attempt.
        // Drop to the manual form with the id pre-filled for correction.
        // If the server says the id references a REAL transfer (verification
        // mismatch rather than not-found), a charge is in play — lock out
        // "no charge found" even for a manual entry.
        const transferExists = TRANSFER_EXISTS_CODES.includes(code)
        setSdkRecovery({ eventId: attemptEventId, attemptId, transferId: null, chargeReported: chargeKnown || transferExists })
        setRecoveryTransferId(transferId)
        setCheckoutError(
          `${message} Enter the correct transfer ID from Finix to resolve this payment.`)
      } else {
        // Network/transient failure — the transfer may well be fine;
        // re-verification is the only allowed action.
        setSdkRecovery({ eventId: attemptEventId, attemptId, transferId, chargeReported: chargeKnown })
        setCheckoutError(
          `${message} The card may already have been charged — retry confirmation, do not start a new payment.`)
      }
      setCheckoutStage('error')
    } finally {
      setWaitingStatus('')
    }
  }

  // SDK-local reader (PAX D135): the phone drives the reader over Bluetooth
  // via the native plugin; the backend verifies the SDK-created transfer
  // before crediting. The attempt is created FIRST so a crash mid-flow
  // leaves a pending row an operator can reconcile.
  const startSdkReaderCharge = async () => {
    if (surfaceActiveSdkCharge()) return
    if (!selectedUser || !selectedDeviceId) return
    if (!isFinixReaderAvailable()) {
      setCheckoutError('This reader is driven by the EventPay app — open Reload in the app to use it.')
      setCheckoutStage('error')
      return
    }
    sdkChargeFlowActive = true
    setCheckoutStage('waiting')
    let attemptId: number | null = null
    // Failures during 'setup' provably precede any charge; once
    // startPayment begins, only known-safe reader codes may cancel.
    let phase: 'setup' | 'payment' = 'setup'
    try {
      setWaitingStatus('Starting…')
      const attempt = await terminalReload.mutateAsync({
        eventId: eventIdNum,
        deviceId: selectedDeviceId,
        userId: selectedUser,
        numTokens: getReloadAmount(),
        idempotencyKey: crypto.randomUUID(),
      })
      if (attempt.state !== 'pending') {
        if (attempt.state === 'succeeded') {
          setCheckoutBalance(attempt.balance ?? null)
          setCheckoutStage('success')
          resetAmounts()
        } else {
          setCheckoutError(
            attempt.failure_message || attempt.failure_code || `Payment ${attempt.state}.`)
          setCheckoutStage('error')
        }
        return
      }
      attemptId = attempt.id
      sdkAttemptIdRef.current = attempt.id
      liveSdkAttemptIds.add(attempt.id)
      // Pre-arm the persisted marker BEFORE the reader can charge: a crash
      // or closed tab from here on lands on the recovery screen, not on a
      // fresh unblocked charge.
      setSdkRecovery({ eventId: eventIdNum, attemptId: attempt.id, transferId: null, chargeReported: false })
      setWaitingStatus('Connecting to reader…')
      const session = await apiClient.getTerminalSdkSession(token ?? '', eventIdNum, selectedDeviceId)
      await FinixReader.initialize(session)
      await FinixReader.connect()
      if (attempt.amount_cents == null) {
        // Setup-phase failure: the attempt is released cleanly. Never fall
        // back to client-side rounding — a one-cent divergence from the
        // backend's cents authority would charge an unconfirmable amount.
        throw new Error('The server did not provide the charge amount — deploy the backend update before using SDK readers.')
      }
      setWaitingStatus('Present card on the reader')
      phase = 'payment'
      const { transferId } = await FinixReader.startPayment({
        // Backend-computed cents: the SDK charges EXACTLY what confirm will
        // verify.
        amountCents: attempt.amount_cents,
        currency: attempt.currency,
        idempotencyKey: crypto.randomUUID(),
        tags: { attempt_id: String(attempt.id), event_id: String(eventIdNum) },
      })
      // A transfer EXISTS from here on — refresh the persisted marker with
      // it before confirmation, so even a crash mid-confirm resumes with
      // the id in hand.
      setSdkRecovery({ eventId: eventIdNum, attemptId: attempt.id, transferId, chargeReported: true })
      setWaitingStatus('')
      await confirmSdkAttempt(eventIdNum, attempt.id, transferId)
    } catch (error) {
      const code = (error as { code?: string }).code ?? ''
      const message = error instanceof Error ? error.message : 'Reader charge failed.'
      const provablyUncharged = phase === 'setup' || READER_SAFE_CANCEL_CODES.includes(code)
      if (attemptId != null && provablyUncharged) {
        // Nothing was charged — release the pending attempt instead of
        // leaking it (best-effort; a canceled attempt can still be
        // confirmed later if a charge somehow DID land), and disarm the
        // pre-armed marker.
        await apiClient.cancelTerminalAttempt(token ?? '', eventIdNum, attemptId).catch(() => {})
        sdkAttemptIdRef.current = null
        setSdkRecovery(null)
        setCheckoutError(message)
      } else if (attemptId != null) {
        // Ambiguous mid-payment failure (incl. cancel racing a tap): the
        // charge may have landed. Hold the attempt and block new charges
        // until settled — never auto-cancel.
        setSdkRecovery({ eventId: eventIdNum, attemptId, transferId: null, chargeReported: false })
        setCheckoutError(
          `${message} The card may have been charged — verify in the Finix dashboard before charging again. This payment is held for confirmation.`)
      } else {
        setCheckoutError(message)
      }
      setCheckoutStage('error')
      setWaitingStatus('')
    } finally {
      sdkChargeFlowActive = false
      if (attemptId != null) liveSdkAttemptIds.delete(attemptId)
    }
  }

  // Manual: records the credit without charging (externally-processed).
  const startManualPayment = async () => {
    if (!selectedUser) return
    // An unresolved SDK card outcome (or one still mid-flight) blocks EVERY
    // payment path — crediting by another method while the card charge is
    // unsettled risks a double.
    if (surfaceActiveSdkCharge() || surfaceSdkRecovery()) return
    setCheckoutStage('manual-processing')
    try {
      await addCredit.mutateAsync({
        eventId: eventIdNum,
        userId: selectedUser,
        amount: getReloadAmount(),
        description: `Standalone card payment - ${formatCurrency(getTotalCost())}`,
      })
      setCheckoutBalance(null)
      setCheckoutStage('success')
      resetAmounts()
    } catch (error) {
      setCheckoutError(apiErrorDetail(error, 'Failed to add credit.'))
      setCheckoutStage('error')
    }
  }

  const handleCashPayment = async () => {
    if (!selectedUser || getReloadAmount() <= 0) return
    
    const received = parseFloat(cashReceived) || 0
    if (received < getTotalCost()) return
    
    setCashError(null)
    setIsProcessing(true)
    setProcessTimeout(15)
    
    const timer = setInterval(() => {
      setProcessTimeout(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          setIsProcessing(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    
    try {
      await addCredit.mutateAsync({
        eventId: eventIdNum,
        userId: selectedUser,
        amount: getReloadAmount(),
        description: `Cash payment - ${formatCurrency(getTotalCost())} (Change: ${formatCurrency(getChange())})`,
      })
      clearInterval(timer)
      setShowCashModal(false)
      setSuccess(true)
      setSelectedAmount(null)
      setCustomAmount('')
      setShowCustomAmount(false)
      setCashReceived('')
      setTimeout(() => setSuccess(false), 3000)
    } catch (error) {
      clearInterval(timer)
      console.error('Failed to add credit:', error)
      setCashError(apiErrorDetail(error, 'Failed to add credit.'))
    } finally {
      setIsProcessing(false)
      setProcessTimeout(0)
    }
  }
  
  const setReloadAmount = (amount: number) => {
    setSelectedAmount(amount)
    setShowCustomAmount(false)
    setCustomAmount('')
  }
  
  const toggleCustomAmount = () => {
    setShowCustomAmount(!showCustomAmount)
    if (!showCustomAmount) {
      setSelectedAmount(null)
    }
  }

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
          Reload Tokens
        </h1>
        {event && (
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            {event.name}
          </p>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3 items-stretch">
        {/* User Search - 1/3 */}
        <Card className="h-full">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-white">Select User</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  type="search"
                  placeholder="Search by name, email, or phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  autoFocus
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowScanner(true)}
                className="shrink-0"
                title="Scan QR Code"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Scan
              </Button>
            </div>

            {qrError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-200 text-sm">
                {qrError}
              </div>
            )}

            {isLoading || (search && search !== debouncedSearch) ? (
              <div className="py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
              </div>
            ) : debouncedSearch && users.length === 0 ? (
              <div className="py-8 text-center text-neutral-500 dark:text-neutral-400">
                No users found
              </div>
            ) : debouncedSearch && users.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {users.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUser(user.id)}
                    className={`w-full flex items-center p-3 rounded-lg border transition-colors ${
                      selectedUser === user.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900/50'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                        <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="text-left flex-1 min-w-0">
                        <p className="font-medium text-neutral-900 dark:text-white truncate">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                          {user.phone_number || user.phone || 'No phone'}
                        </p>
                      </div>
                      {typeof user.balance === 'number' && (
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                            {Math.round(user.balance)}
                          </p>
                          <p className="text-xs text-neutral-400">tokens</p>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-8 text-sm text-center text-neutral-500 dark:text-neutral-400">
                Search for a user to add credit
              </div>
            )}
          </CardContent>
        </Card>

        <QRScannerModal
          isOpen={showScanner}
          onScan={handleQrScan}
          onClose={() => setShowScanner(false)}
          title="Scan User QR Code"
        />

        {/* Reload Form - 2/3 */}
        <div className="lg:col-span-2 h-full">
          {selectedUserData ? (
            <Card>
              <CardContent className="pt-6 space-y-6">
                {/* User Info Row */}
                <div className="flex items-center justify-between pb-4 border-b border-neutral-200 dark:border-neutral-800">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg">
                      {selectedUserData.first_name?.charAt(0) || ''}{selectedUserData.last_name?.charAt(0) || ''}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-neutral-900 dark:text-white">
                        {selectedUserData.first_name} {selectedUserData.last_name}
                      </h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {selectedUserData.phone_number || selectedUserData.phone || 'No phone'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">Balance</p>
                      <p className="font-semibold text-neutral-900 dark:text-white">
                        {userMetrics?.balance_tokens || 0} Tokens
                      </p>
                    </div>
                    <button
                      onClick={() => openUserModal(selectedUserData.id, eventIdNum)}
                      className="p-2 border border-neutral-200 dark:border-neutral-700 rounded-md text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                      title="View User"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="px-3 py-2 bg-neutral-600 hover:bg-neutral-700 text-white text-xs rounded-md font-medium transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                
                {/* Select Amount */}
                <div>
                  <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Select Amount</h4>
                  {/* Amount Buttons */}
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {topupAmounts.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setReloadAmount(amount)}
                        className={`px-2 py-3 border rounded-md text-xs font-medium transition-colors ${
                          selectedAmount === amount
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 border-neutral-300 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                        }`}
                      >
                        {amount} Tokens
                      </button>
                    ))}
                    {/* The Custom button IS the input once active — no
                        separate row below eating vertical space. */}
                    {showCustomAmount ? (
                      <input
                        type="number"
                        autoFocus
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        onBlur={() => { if (!customAmount) setShowCustomAmount(false) }}
                        placeholder="Tokens…"
                        min="1"
                        className="px-2 py-3 border rounded-md text-sm font-medium text-center bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 border-blue-600 ring-1 ring-blue-600 focus:outline-none"
                      />
                    ) : (
                      <button
                        onClick={toggleCustomAmount}
                        className="px-2 py-3 border rounded-md text-sm font-medium transition-colors bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 border-neutral-300 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      >
                        Custom
                      </button>
                    )}
                  </div>
                </div>
                  
                {/* Summary */}
                  <div className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-md p-4">
                    <div className="space-y-2">
                      {/* Amount Row */}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">Amount:</span>
                        <div className="font-semibold text-neutral-900 dark:text-neutral-100">
                          {getReloadAmount()} Tokens
                        </div>
                      </div>
                      
                      {/* Payment Method Row */}

                      {/* Cost Breakdown */}
                      <div className="border-t border-neutral-200 dark:border-neutral-800 pt-2 space-y-2">
                        {/* Base Cost */}
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-neutral-600 dark:text-neutral-400">Base Cost:</span>
                          <span className="text-neutral-900 dark:text-neutral-100">
                            {formatCurrency(getBaseCost())}
                          </span>
                        </div>
                        
                        {/* Tax (if applicable) */}
                        {getTaxAmount() > 0 && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-neutral-600 dark:text-neutral-400">
                              Tax ({(taxPercentage * 100).toFixed(1)}%)
                            </span>
                            <span className="text-neutral-900 dark:text-neutral-100">
                              {formatCurrency(getTaxAmount())}
                            </span>
                          </div>
                        )}
                        
                        {/* Reload Fee */}
                        {getTotalAdditionalFees() > 0 && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-neutral-600 dark:text-neutral-400">Reload Fee:</span>
                            <span className="text-neutral-900 dark:text-neutral-100">
                              {formatCurrency(getTotalAdditionalFees())}
                            </span>
                          </div>
                        )}
                        
                        {/* Total */}
                        <div className="flex justify-between items-center text-base font-semibold border-t border-neutral-200 dark:border-neutral-800 pt-2">
                          <span className="text-neutral-900 dark:text-neutral-100">Total:</span>
                          <span className="text-neutral-900 dark:text-neutral-100">
                            {formatCurrency(getTotalCost())} {currency}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {success && (
                    <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center gap-2 text-green-600 dark:text-green-400">
                      <Check className="w-5 h-5" />
                      Credit added successfully!
                    </div>
                  )}

                  {/* One button; method choice happens in the checkout modal */}
                  {canRefundAddcredit ? (
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        setCheckoutError('')
                        setCheckoutBalance(null)
                        setCheckoutStage('method')
                        setShowCheckout(true)
                      }}
                      disabled={getReloadAmount() <= 0}
                    >
                      Checkout
                    </Button>
                  ) : (
                    <p className="text-center text-sm text-neutral-500 dark:text-neutral-400">
                      You don't have permission to add credit.
                    </p>
                  )}
                </CardContent>
              </Card>
          ) : (
            <Card className="h-full flex flex-col justify-center">
              <CardContent className="py-12 text-center text-neutral-500 dark:text-neutral-400">
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a user to add credit</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Checkout Modal — method choice, terminal status, success, errors */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              if (checkoutStage !== 'waiting' && checkoutStage !== 'manual-processing') {
                setShowCheckout(false)
              }
            }}
          />
          <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl">
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-2">
                  {checkoutStage === 'terminal' && (
                    <button
                      onClick={() => setCheckoutStage('method')}
                      className="-ml-1 text-neutral-500 hover:text-neutral-900 dark:hover:text-white"
                      aria-label="Back to payment methods"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                  )}
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    Checkout
                  </h3>
                </div>
                {checkoutStage !== 'waiting' && checkoutStage !== 'manual-processing' && (
                  <button
                    onClick={() => setShowCheckout(false)}
                    className="text-neutral-500 hover:text-neutral-900 dark:hover:text-white"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                )}
              </div>


              {checkoutStage === 'method' && (
                <>
                  <div className="bg-neutral-50 dark:bg-neutral-800/60 rounded-md p-4 mb-5 flex justify-between items-baseline">
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">Total</span>
                    <span className="text-xl font-bold text-green-700 dark:text-green-400">
                      {formatCurrency(getTotalCost())}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
                    Select payment method:
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        // Same block as card/standalone: settle the open SDK
                        // charge before crediting through any other path.
                        if (surfaceActiveSdkCharge() || surfaceSdkRecovery()) return
                        setShowCheckout(false)
                        setCashReceived('')
                        setShowCashModal(true)
                      }}
                      className="flex flex-col items-center gap-3 p-6 border border-neutral-200 dark:border-neutral-700 rounded-xl hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-950/30 transition-colors"
                    >
                      <span className="w-12 h-12 rounded-xl bg-green-600 text-white flex items-center justify-center"><DollarSign className="w-6 h-6" /></span>
                      <span className="text-base font-medium text-neutral-900 dark:text-neutral-100">Cash</span>
                    </button>
                    {terminals && terminals.length > 0 && (
                      <button
                        onClick={() => setCheckoutStage('terminal')}
                        className="flex flex-col items-center gap-3 p-6 border border-neutral-200 dark:border-neutral-700 rounded-xl hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
                      >
                        <span className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center"><CreditCard className="w-6 h-6" /></span>
                        <span className="text-base font-medium text-neutral-900 dark:text-neutral-100">Terminal</span>
                      </button>
                    )}
                    <button
                      onClick={() => startManualPayment()}
                      title="Records the credit without charging — for cards processed on a standalone machine"
                      className="flex flex-col items-center gap-3 p-6 border border-neutral-200 dark:border-neutral-700 rounded-xl hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-950/30 transition-colors"
                    >
                      <span className="w-12 h-12 rounded-xl bg-purple-600 text-white flex items-center justify-center"><CreditCard className="w-6 h-6" /></span>
                      <span className="text-base font-medium text-neutral-900 dark:text-neutral-100">Standalone</span>
                    </button>
                  </div>
                </>
              )}

              {checkoutStage === 'terminal' && (
                <>
                  <div className="bg-neutral-50 dark:bg-neutral-800/60 rounded-md p-4 mb-5 flex justify-between items-baseline">
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">Total</span>
                    <span className="text-xl font-bold text-green-700 dark:text-green-400">
                      {formatCurrency(getTotalCost())}
                    </span>
                  </div>
                  <label className="text-xs text-neutral-500 dark:text-neutral-400">
                    Terminal
                    <select
                      className="block w-full mt-1 mb-4 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
                      value={selectedDeviceId ?? ''}
                      onChange={(e) => setSelectedDeviceId(Number(e.target.value) || null)}
                    >
                      <option value="">Select a terminal…</option>
                      {terminals?.map((device) => {
                        const sdkLocal = (device.capabilities as Record<string, unknown> | undefined)?.interaction === 'sdk_local'
                        const appOnly = sdkLocal && !isFinixReaderAvailable()
                        return (
                          <option key={device.id} value={device.id} disabled={appOnly}>
                            {device.name}
                            {appOnly
                              ? ' — requires the EventPay app'
                              : device.last_connection ? ` — ${device.last_connection.toLowerCase() === 'open' ? 'connected' : device.last_connection}` : ''}
                          </option>
                        )
                      })}
                    </select>
                  </label>
                  <Button
                    className="mt-2 w-full bg-green-600 hover:bg-green-700"
                    disabled={!selectedDeviceId}
                    onClick={startTerminalCharge}
                  >
                    Charge {formatCurrency(getTotalCost())}
                  </Button>
                </>
              )}

              {(checkoutStage === 'waiting' || checkoutStage === 'manual-processing') && (
                <div className="text-center py-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mb-5">
                    <span className="w-7 h-7 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                  <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    {checkoutStage === 'waiting' ? 'Insert, tap, or swipe card' : 'Recording payment…'}
                  </p>
                  {checkoutStage === 'waiting' && (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                      {waitingStatus || 'Follow the prompts on the card terminal.'}
                    </p>
                  )}
                  <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mt-4 mb-6">
                    {formatCurrency(getTotalCost())}
                  </p>
                  {checkoutStage === 'waiting' && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={async () => {
                        try {
                          if (isSdkDevice) {
                            // Stop the reader locally, then cancel THIS
                            // attempt only — device-wide cancel would sweep
                            // up other pending attempts on the terminal.
                            await FinixReader.cancel().catch(() => {})
                            if (sdkAttemptIdRef.current != null) {
                              await apiClient.cancelTerminalAttempt(
                                token ?? '', eventIdNum, sdkAttemptIdRef.current)
                            }
                          } else if (selectedDeviceId) {
                            // Server-push: the blocking reload has no attempt
                            // id client-side, so cancel is device-addressed.
                            await apiClient.cancelTerminalInteraction(token ?? '', eventIdNum, selectedDeviceId)
                          }
                        } catch {
                          // terminal may already have resolved — the charge
                          // request reports whichever outcome won
                        }
                      }}
                    >
                      Cancel Payment
                    </Button>
                  )}
                </div>
              )}

              {checkoutStage === 'success' && (
                <div className="text-center py-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center mb-5">
                    <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    Payment complete
                  </p>
                  {checkoutBalance != null && (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                      New balance: {checkoutBalance} tokens
                    </p>
                  )}
                  <Button className="w-full mt-6" onClick={() => setShowCheckout(false)}>
                    Done
                  </Button>
                </div>
              )}

              {checkoutStage === 'error' && (
                <div className="text-center py-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center mb-5">
                    <span className="text-2xl text-red-600 dark:text-red-400">✕</span>
                  </div>
                  <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    Payment failed
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400">{checkoutError}</p>
                  {sdkRecovery && recoveryIsLive ? (
                    // The reader operation is STILL RUNNING in this tab (an
                    // event switch mid-tap). No resolution is allowed while
                    // the outcome can still change — only canceling the
                    // reader itself; the running flow settles the marker.
                    <div className="mt-6 space-y-3">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => { FinixReader.cancel().catch(() => {}) }}
                      >
                        Cancel reader payment
                      </Button>
                      <Button variant="outline" className="w-full" onClick={() => setShowCheckout(false)}>
                        Close
                      </Button>
                    </div>
                  ) : sdkRecovery && !sdkRecovery.transferId ? (
                    // Ambiguous — no transfer id was reported. Two REAL
                    // resolutions, both settling the attempt server-side:
                    // confirm with the transfer id found at Finix, or
                    // cancel because no charge exists. Clearing the block
                    // without either is deliberately not offered.
                    <div className="mt-6 space-y-3 text-left">
                      <label className="text-xs text-neutral-500 dark:text-neutral-400">
                        Transfer ID from the Finix dashboard
                        <Input
                          value={recoveryTransferId}
                          onChange={(e) => setRecoveryTransferId(e.target.value)}
                          placeholder="TR…"
                          className="mt-1 font-mono"
                        />
                      </label>
                      {sdkRecovery.chargeReported ? (
                        // A transfer WAS reported — a charge exists, so the
                        // only resolution is confirming the correct id.
                        <Button
                          className="w-full"
                          disabled={!recoveryTransferId.trim()}
                          onClick={() => confirmSdkAttempt(sdkRecovery.eventId, sdkRecovery.attemptId, recoveryTransferId.trim(), true)}
                        >
                          Confirm transfer
                        </Button>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <Button variant="outline" onClick={resolveNoChargeFound}>
                            No charge found — cancel
                          </Button>
                          <Button
                            disabled={!recoveryTransferId.trim()}
                            onClick={() => confirmSdkAttempt(sdkRecovery.eventId, sdkRecovery.attemptId, recoveryTransferId.trim(), false)}
                          >
                            Confirm transfer
                          </Button>
                        </div>
                      )}
                      <Button variant="outline" className="w-full" onClick={() => setShowCheckout(false)}>
                        Close
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 mt-6">
                      <Button variant="outline" onClick={() => setShowCheckout(false)}>
                        Close
                      </Button>
                      {sdkRecovery?.transferId ? (
                        // A transfer was reported by the reader — the card is
                        // charged. Only re-verification is allowed from here.
                        <Button onClick={() => confirmSdkAttempt(sdkRecovery.eventId, sdkRecovery.attemptId, sdkRecovery.transferId!, sdkRecovery.chargeReported)}>
                          Retry confirmation
                        </Button>
                      ) : (
                        <Button onClick={() => { setCheckoutError(''); setCheckoutStage('method') }}>
                          Try again
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cash Payment Modal */}
      {showCashModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isProcessing && setShowCashModal(false)}
          />
          
          {/* Modal */}
          <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl">
            <div className="p-6">
              <h3 className="flex items-center text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-6">
                <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                Cash Payment
              </h3>
              
              <div className="space-y-6">
                {/* Total Charge Display */}
                <div className="bg-neutral-50 dark:bg-neutral-800 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-medium text-neutral-700 dark:text-neutral-300">Total Charge:</span>
                    <span className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                      {formatCurrency(getTotalCost())}
                    </span>
                  </div>
                </div>
                
                {/* Quick Amount Buttons */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                    Quick Amounts
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {getQuickCashAmounts().map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setCashReceived(amount.toFixed(2))}
                        className={`px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
                          parseFloat(cashReceived) === amount
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                        }`}
                      >
                        {formatCurrency(amount)}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Cash Received Input */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Cash Received
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                    <Input
                      type="number"
                      value={cashReceived}
                      onChange={(e) => setCashReceived(e.target.value)}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="pl-8"
                    />
                  </div>
                </div>
                
                {/* Change Calculation */}
                <div className="bg-neutral-50 dark:bg-neutral-800 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-medium text-neutral-700 dark:text-neutral-300">Change:</span>
                    <span className={`text-xl font-bold ${
                      (parseFloat(cashReceived) || 0) >= getTotalCost()
                        ? 'text-green-600 dark:text-green-500'
                        : 'text-red-600 dark:text-red-500'
                    }`}>
                      {formatCurrency(getChange())}
                    </span>
                  </div>
                  
                  {/* Insufficient Payment Warning */}
                  {parseFloat(cashReceived) > 0 && parseFloat(cashReceived) < getTotalCost() && (
                    <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm text-red-800 dark:text-red-200 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Insufficient payment amount
                      </p>
                    </div>
                  )}
                  {cashError && (
                    <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm text-red-800 dark:text-red-200">{cashError}</p>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowCashModal(false)}
                    disabled={isProcessing}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCashPayment}
                    disabled={(parseFloat(cashReceived) || 0) < getTotalCost() || isProcessing}
                    isLoading={isProcessing}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-neutral-300 dark:disabled:bg-neutral-700"
                  >
                    {isProcessing ? `Processing... (${processTimeout}s)` : 'Process Payment'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
